/**
 * Reset Password Page — Updates user password after clicking Supabase recovery email link.
 */

import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Lock, ArrowRight, AlertCircle, CheckCircle2, ShieldCheck } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { siteConfig } from "@/config/site";

export const Route = createFileRoute("/account/reset-password")({
  head: () => ({
    meta: [
      { title: `Set New Password — ${siteConfig.name}` },
      // noindex,follow — see the note in account/login.tsx: crawl allowed, indexing refused.
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: ResetPasswordPage,
});

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [isSessionValid, setIsSessionValid] = useState<boolean | null>(null);

  useEffect(() => {
    const mounted = true;

    async function checkRecoverySession() {
      // Supabase auto-establishes a session when user lands from email recovery link
      const { data } = await supabase.auth.getSession();
      if (mounted) {
        if (data.session) {
          setIsSessionValid(true);
        } else {
          // Listen for onAuthStateChange PASSWORD_RECOVERY event
          const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === "PASSWORD_RECOVERY" || session) {
              if (mounted) setIsSessionValid(true);
            }
          });

          // Give time for Supabase hash token exchange
          setTimeout(async () => {
            const { data: retryData } = await supabase.auth.getSession();
            if (mounted && !retryData.session) {
              setIsSessionValid(false);
            }
          }, 1500);

          return () => {
            authListener.subscription.unsubscribe();
          };
        }
      }
    }

    checkRecoverySession();
  }, []);

  const validatePassword = (pass: string): string | null => {
    if (pass.length < 8) return "Password must be at least 8 characters long.";
    if (!/[A-Z]/.test(pass)) return "Password must contain at least one uppercase letter.";
    if (!/[a-z]/.test(pass)) return "Password must contain at least one lowercase letter.";
    if (!/[0-9]/.test(pass)) return "Password must contain at least one number.";
    return null;
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg(null);
    setSuccessNotice(null);

    const passErr = validatePassword(newPassword);
    if (passErr) {
      setErrorMsg(passErr);
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg("Passwords do not match. Please re-enter your password.");
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        setErrorMsg(error.message || "Failed to update password. Link may have expired.");
      } else {
        setSuccessNotice("Your password has been successfully updated!");
        setTimeout(() => {
          navigate({ to: "/account/login" as never });
        }, 2500);
      }
    } catch {
      setErrorMsg("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="section-container pb-28 pt-24 md:pt-28 max-w-md mx-auto">
      <div className="text-center">
        <p className="eyebrow">Secure Account</p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl">Set New Password</h1>
        <p className="mt-2 text-xs text-muted-foreground">
          Create a new strong password for your account.
        </p>
      </div>

      <div className="mt-8 border bg-card p-6 sm:p-8">
        {errorMsg && (
          <div className="mb-5 flex items-center gap-2 text-xs text-destructive bg-destructive/10 p-3 rounded border border-destructive/20">
            <AlertCircle className="h-4 w-4 shrink-0 text-destructive" />
            <span>{errorMsg}</span>
          </div>
        )}

        {successNotice && (
          <div className="mb-5 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-100 p-3 rounded border border-emerald-500/20">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            <span>{successNotice} Redirecting to login...</span>
          </div>
        )}

        {isSessionValid === false ? (
          <div className="text-center space-y-4">
            <AlertCircle className="h-10 w-10 text-amber-500 mx-auto" />
            <p className="text-xs text-muted-foreground">
              The password reset link is invalid or has expired. Please request a new link.
            </p>
            <Link
              to="/account/forgot-password"
              className="inline-flex min-h-11 w-full items-center justify-center bg-primary text-xs font-medium uppercase tracking-wider text-primary-foreground"
            >
              Request New Reset Link
            </Link>
          </div>
        ) : (
          <form onSubmit={handleUpdatePassword} className="space-y-5">
            <div>
              <label htmlFor="newPassword" className="eyebrow block">
                New Password
              </label>
              <div className="relative mt-2">
                <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="newPassword"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Min 8 chars (uppercase, lowercase, number)"
                  className="min-h-11 w-full border border-border bg-background pl-10 pr-4 text-xs outline-none focus:border-primary"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="eyebrow block">
                Confirm New Password
              </label>
              <div className="relative mt-2">
                <Lock className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  className="min-h-11 w-full border border-border bg-background pl-10 pr-4 text-xs outline-none focus:border-primary"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || Boolean(successNotice)}
              className="flex min-h-12 w-full items-center justify-center gap-2 bg-primary text-xs tracking-[0.2em] uppercase text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {loading ? "Updating Password..." : "Update Password"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
